import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import SyncChip from "@/components/SyncChip";
import OfflineIndicator from "@/components/OfflineIndicator";
import InstallPrompt from "@/components/InstallPrompt";
import { cacheList, cachePut, cacheGet } from "@/lib/refcache";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Truck, SignIn, NotePencil, QrCode, CaretRight, HardHat, Clock, ShieldWarning, SignOut, LockKey, Coffee } from "@phosphor-icons/react";

const TYPE_META = {
  docket: { label: "Hire Docket", icon: Truck },
  signin: { label: "Sign In / Out", icon: SignIn },
  diary: { label: "Site Diary", icon: NotePencil },
};

const readWorker = () => {
  try { return JSON.parse(localStorage.getItem("eq_field_worker") || "null"); } catch { return null; }
};

export default function FieldHub() {
  const [recent, setRecent] = useState([]);
  const [worker, setWorker] = useState(readWorker);
  const [home, setHome] = useState(null);
  const [stale, setStale] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [matches, setMatches] = useState(null);

  const loadHome = useCallback((w) => {
    if (!w || !w.id || !w.token) return;
    api.get(`/public/worker-home/${w.id}`, { params: { token: w.token } })
      .then(({ data }) => { setHome(data); setStale(false); cachePut(`home:${w.id}`, data, null).catch(() => {}); })
      .catch(async (e) => {
        if (!e.response) {
          const hit = await cacheGet(`home:${w.id}`).catch(() => null);
          if (hit) { setHome(hit.data); setStale(true); }
        } else if (e.response.status === 403) {
          localStorage.removeItem("eq_field_worker"); setWorker(null); setHome(null);
        }
      });
  }, []);

  useEffect(() => { if (worker) loadHome(worker); }, [worker, loadHome]);

  const [signingOut, setSigningOut] = useState(false);
  const [nowTs, setNowTs] = useState(Date.now());
  useEffect(() => {
    if (!home?.active_session?.sign_in_at) return;
    const t = setInterval(() => setNowTs(Date.now()), 30000);
    setNowTs(Date.now());
    return () => clearInterval(t);
  }, [home?.active_session?.sign_in_at]);
  const elapsedLabel = (iso) => {
    if (!iso) return "";
    const mins = Math.max(0, Math.floor((nowTs - new Date(iso).getTime()) / 60000));
    const h = Math.floor(mins / 60), m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };
  const signOutSession = async () => {
    const sess = home?.active_session;
    if (!sess?.project_id || !worker) return;
    setSigningOut(true);
    try {
      await api.post(`/public/project-signin/${sess.project_id}`, {
        worker_id: worker.id, field_token: worker.token,
        client_ref: (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())),
        captured_at: new Date().toISOString(),
      });
      loadHome(worker);
    } catch { /* offline / already out — refresh anyway */ loadHome(worker); }
    setSigningOut(false);
  };

  const [breaking, setBreaking] = useState(false);
  const [activityPeriod, setActivityPeriod] = useState("today");
  const [activity, setActivity] = useState([]);
  useEffect(() => {
    if (!worker?.id || !worker?.token) return;
    api.get(`/public/worker-activity/${worker.id}`, { params: { token: worker.token, period: activityPeriod } })
      .then(({ data }) => setActivity(data.events || []))
      .catch(() => {});
  }, [worker, activityPeriod, home]);
  const fmtWhen = (ev) => {
    const t = ev.at ? new Date(ev.at) : null;
    if (!t || isNaN(t.getTime())) return ev.date || "";
    const time = t.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
    return activityPeriod === "today" ? time : `${t.toLocaleDateString("en-AU", { weekday: "short" })} ${time}`;
  };
  const [viewDocket, setViewDocket] = useState(null);
  const openDocket = (id) => {
    if (!id || !worker) return;
    setViewDocket({ loading: true });
    api.get(`/public/worker-docket/${id}`, { params: { worker_id: worker.id, token: worker.token } })
      .then(({ data }) => setViewDocket(data))
      .catch(() => setViewDocket({ error: true }));
  };
  const DK_STATUS = {
    submitted: { label: "Submitted", cls: "bg-blue-500/20 text-blue-200 border-blue-400/40" },
    approved: { label: "Approved", cls: "bg-green-500/20 text-green-200 border-green-400/40" },
    rejected: { label: "Rejected", cls: "bg-red-500/20 text-red-200 border-red-400/40" },
    draft: { label: "Draft", cls: "bg-gray-500/20 text-gray-200 border-gray-400/40" },
  };
  const toggleBreak = async () => {
    const sess = home?.active_session;
    if (!sess?.project_id || !worker) return;
    setBreaking(true);
    try {
      await api.post(`/public/attendance/break/${sess.project_id}`, { worker_id: worker.id, field_token: worker.token });
      loadHome(worker);
    } catch { loadHome(worker); }
    setBreaking(false);
  };

  useEffect(() => {
    cacheList().then((entries) => {
      const items = entries.map((e) => {
        if (e.key.startsWith("docket:"))
          return { type: "docket", title: e.data.asset?.asset_no || "Asset", sub: e.data.asset?.asset_type || "", url: `/docket?asset=${e.key.slice(7)}`, at: e.cached_at };
        if (e.key.startsWith("signin:"))
          return { type: "signin", title: e.data.project_name || "Site", sub: e.data.site_address || "", url: `/sign-in?project=${e.key.slice(7)}`, at: e.cached_at };
        if (e.key.startsWith("diary:"))
          return { type: "diary", title: e.data.payload?.project_name || "Site", sub: `Last opened ${e.data.date || ""}`, url: `/site-diary?project=${e.key.slice(6)}`, at: e.cached_at };
        return null;
      }).filter(Boolean).sort((a, b) => (b.at || "").localeCompare(a.at || ""));
      setRecent(items);
    }).catch(() => {});
  }, []);

  const identify = async (workerId) => {
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/public/worker-identify", workerId ? { pin: pin.trim(), worker_id: workerId } : { pin: pin.trim() });
      if (data.ambiguous) { setMatches(data.matches); setBusy(false); return; }
      const w = { id: data.worker_id, name: data.name, token: data.field_token };
      localStorage.setItem("eq_field_worker", JSON.stringify(w));
      setMatches(null); setPin(""); setWorker(w);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't verify that PIN. Try again.");
    }
    setBusy(false);
  };

  const signOutDevice = () => {
    localStorage.removeItem("eq_field_worker");
    setWorker(null); setHome(null); setPin(""); setMatches(null); setError("");
  };

  const firstName = (home?.worker_name || worker?.name || "").split(" ")[0];
  const Row = ({ k, v }) => (
    <div className="flex justify-between gap-3">
      <span className="text-gray-500">{k}</span>
      <span className="text-equipr-black font-medium text-right">{v}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-equipr-black flex flex-col" data-testid="field-hub">
      <OfflineIndicator />
      <InstallPrompt />
      <div className="px-5 py-4 flex items-center justify-between">
        <BrandLogo variant="dark" showProduct={false} />
        <span className="flex items-center gap-2"><SyncChip /><span className="text-xs font-bold uppercase tracking-widest text-equipr-yellow">Field</span></span>
      </div>

      <div className="flex-1 px-5 pb-8">
        {!worker ? (
          /* ---- Identity gate ---- */
          <div className="mt-4" data-testid="field-identify">
            <h1 className="text-2xl font-extrabold text-white">Who's on the tools?</h1>
            <p className="text-sm text-gray-400 mt-1">Enter your Worker PIN to load your jobs, hours and dockets on this phone.</p>

            {error && <div className="mt-4 bg-red-500/15 border border-red-500/40 text-red-200 text-sm px-3 py-2 rounded-[8px]" data-testid="field-identify-error">{error}</div>}

            {matches ? (
              <div className="mt-5" data-testid="field-identify-matches">
                <p className="text-[12px] font-bold uppercase tracking-widest text-gray-400 mb-2">Confirm who you are</p>
                <div className="space-y-2">
                  {matches.map((m) => (
                    <button key={m.worker_id} onClick={() => identify(m.worker_id)} disabled={busy} data-testid={`field-identify-pick-${m.worker_id}`}
                      className="w-full flex items-center gap-3 bg-white/5 border border-white/10 hover:border-equipr-yellow px-4 py-3 text-left">
                      <HardHat size={18} className="text-equipr-yellow shrink-0" weight="bold" />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-bold text-white truncate">{m.name}</span>
                        {m.company && <span className="block text-[11px] text-gray-400 truncate">{m.company}</span>}
                      </span>
                      <CaretRight size={16} className="text-gray-500 shrink-0" />
                    </button>
                  ))}
                </div>
                <button onClick={() => { setMatches(null); setError(""); }} className="mt-3 text-xs text-gray-500 hover:text-equipr-yellow">← Back</button>
              </div>
            ) : (
              <div className="mt-5 bg-white p-4 rounded-[4px]">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1"><LockKey size={12} /> Worker PIN</label>
                <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
                  inputMode="numeric" placeholder="••••••" data-testid="field-identify-pin"
                  onKeyDown={(e) => e.key === "Enter" && pin && identify()}
                  className="w-full h-[54px] px-[15px] border-[1.5px] border-border-control bg-white text-center text-2xl tracking-[0.5em] rounded-[12px] focus:border-ink-primary focus:outline-none focus:shadow-[0_0_0_3px_rgba(255,209,8,0.35)]" />
                <button onClick={() => identify()} disabled={busy || !pin} data-testid="field-identify-submit"
                  className="w-full h-[54px] mt-4 bg-equipr-yellow text-equipr-black font-bold text-[16px] uppercase tracking-[0.04em] rounded-[12px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px disabled:opacity-60">
                  {busy ? "Checking…" : "Continue"}
                </button>
                <p className="text-[11px] text-gray-400 mt-2 text-center">Don't know your PIN? Ask your site foreman. Just visiting? Use the site's Sign-In QR.</p>
              </div>
            )}
          </div>
        ) : (
          /* ---- Identified worker ---- */
          <>
            <div className="mt-2 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-widest text-equipr-yellow flex items-center gap-1.5">
                  <HardHat size={14} weight="bold" /> G'day {firstName}
                  {stale && <span className="font-semibold normal-case tracking-normal text-gray-500">· saved {home?.date || ""}</span>}
                </p>
                <h1 className="text-2xl font-extrabold text-white mt-0.5">Your day</h1>
              </div>
              <button onClick={signOutDevice} data-testid="field-signout-device"
                className="shrink-0 inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-equipr-yellow border border-white/15 px-2.5 py-1.5 rounded-[7px]">
                <SignOut size={13} weight="bold" /> Not you?
              </button>
            </div>

            {/* On-site status + one-tap finish */}
            {home?.active_session && (() => {
              const sess = home.active_session;
              const onBreak = sess.on_break;
              return (
              <div className={`mt-4 p-4 border ${onBreak ? "bg-amber-500/15 border-amber-500/40" : "bg-green-500/15 border-green-500/40"}`} data-testid="field-onsite">
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${onBreak ? "bg-amber-400" : "bg-green-400 animate-pulse"}`} />
                  <p className="text-[13px] font-bold text-white flex-1 min-w-0 truncate">{onBreak ? "On break" : "On site"} — {sess.project_name}</p>
                  <span data-testid="field-onsite-timer" className={`shrink-0 inline-flex items-center gap-1 text-[12px] font-bold tabular-nums px-2 py-0.5 rounded-full border ${onBreak ? "bg-amber-400/20 text-amber-100 border-amber-400/40" : "bg-green-400/20 text-green-200 border-green-400/40"}`}>
                    <Clock size={12} weight="bold" /> {elapsedLabel(sess.sign_in_at)}
                  </span>
                </div>
                <p className="text-[11px] text-white/70 mt-0.5">
                  Signed in{sess.sign_in_at ? ` at ${new Date(sess.sign_in_at).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}` : ""}
                  {sess.break_minutes > 0 ? ` · ${sess.break_minutes} min break${sess.break_minutes === 1 ? "" : "s"}` : ""}
                </p>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button onClick={toggleBreak} disabled={breaking} data-testid="field-break-toggle"
                    className={`h-11 font-extrabold text-[13px] uppercase tracking-[0.03em] flex items-center justify-center gap-1.5 active:translate-y-px disabled:opacity-60 ${onBreak ? "bg-equipr-yellow text-equipr-black hover:bg-equipr-yellow-dark" : "bg-white/10 text-white border border-white/25 hover:bg-white/15"}`}>
                    <Coffee size={15} weight="bold" /> {breaking ? "…" : onBreak ? "Back on" : "Take a break"}
                  </button>
                  <button onClick={signOutSession} disabled={signingOut} data-testid="field-signout-session"
                    className="h-11 bg-white text-equipr-black font-extrabold text-[13px] uppercase tracking-[0.03em] flex items-center justify-center gap-1.5 hover:bg-gray-100 active:translate-y-px disabled:opacity-60">
                    <SignOut size={15} weight="bold" /> {signingOut ? "…" : "Finish"}
                  </button>
                </div>
              </div>
              );
            })()}

            {/* Weekly hours */}
            {home && (
              <Link to="/my-timesheet" data-testid="field-week-hours"
                className="mt-4 flex items-center gap-3 bg-white/5 border border-white/10 hover:border-equipr-yellow px-4 py-3">
                <span className="w-9 h-9 bg-equipr-yellow text-equipr-black flex items-center justify-center shrink-0"><Clock size={18} weight="bold" /></span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-bold text-white">{home.week_hours} h this week{home.on_site_now ? " · on site now" : ""}</span>
                  <span className="block text-[11px] text-gray-400">Tap to review &amp; submit your timesheet</span>
                </span>
                <CaretRight size={16} className="text-gray-500 shrink-0" />
              </Link>
            )}

            {/* Roster + rostered plant */}
            {home?.roster?.length > 0 ? (
              <div className="mt-4 space-y-3" data-testid="field-roster">
                {home.roster.map((r, i) => (
                  <div key={r.project_id} className="bg-equipr-yellow text-equipr-black p-3" data-testid={`field-roster-item-${i}`}>
                    <p className="text-[11px] font-bold uppercase tracking-widest truncate">You're rostered — {r.project_name}</p>
                    <Link to={`/sign-in?project=${r.project_id}`} data-testid={`field-signin-${i}`}
                      className="mt-2 flex items-center gap-2 bg-equipr-black text-white px-3 py-2.5 hover:bg-black">
                      <SignIn size={16} weight="bold" className="text-equipr-yellow" />
                      <span className="flex-1 text-sm font-bold">Sign in / out on site</span>
                      <CaretRight size={12} weight="bold" className="text-equipr-yellow" />
                    </Link>
                    {r.plant?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-equipr-black/60 mb-1">Your plant — tap to fill a docket</p>
                        <div className="grid grid-cols-2 gap-1.5">
                          {r.plant.map((p) => (
                            <Link key={p.asset_id} to={`/docket?asset=${p.asset_id}`} data-testid={`field-plant-${p.asset_id}`}
                              className="flex items-center gap-1.5 bg-white border border-equipr-black/15 px-2 py-2 hover:border-equipr-black">
                              <Truck size={14} weight="bold" className="shrink-0" />
                              <span className="text-[12px] font-bold truncate">{p.asset_no}</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : home ? (
              <p className="mt-4 bg-white/5 border border-white/10 text-sm text-gray-300 px-4 py-3" data-testid="field-roster-none">
                No allocations for you today. Scan a machine's QR to fill a docket, or a site sign to sign in.
              </p>
            ) : null}

            {/* Compliance / tickets */}
            {home?.compliance?.length > 0 && (
              <div className="mt-4" data-testid="field-compliance">
                <p className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-1.5">My tickets &amp; compliance</p>
                <div className="space-y-1.5">
                  {home.compliance.map((c, i) => {
                    const bad = c.status === "expired";
                    const warn = c.status === "expiring";
                    return (
                      <div key={i} data-testid={`field-compliance-${i}`}
                        className={`flex items-center gap-2 px-3 py-2 border ${bad ? "bg-red-500/15 border-red-500/40" : warn ? "bg-amber-500/15 border-amber-500/40" : "bg-white/5 border-white/10"}`}>
                        {(bad || warn) && <ShieldWarning size={16} weight="bold" className={bad ? "text-red-300" : "text-amber-300"} />}
                        <span className="flex-1 min-w-0">
                          <span className="block text-[13px] font-bold text-white truncate">{c.type}{c.ref ? ` · ${c.ref}` : ""}</span>
                          {c.expiry_date && <span className={`block text-[11px] ${bad ? "text-red-300" : warn ? "text-amber-300" : "text-gray-400"}`}>{bad ? "Expired" : warn ? "Expiring" : "Valid to"} {c.expiry_date}</span>}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Activity log */}
            <div className="mt-5" data-testid="field-activity">
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[11px] font-bold uppercase tracking-widest text-gray-500">Activity</p>
                <div className="inline-flex rounded-full border border-white/15 overflow-hidden">
                  {[["today", "Today"], ["this_week", "This week"], ["last_week", "Last week"]].map(([k, l]) => (
                    <button key={k} onClick={() => setActivityPeriod(k)} data-testid={`field-activity-${k}`}
                      className={`px-2.5 py-1 text-[11px] font-bold ${activityPeriod === k ? "bg-equipr-yellow text-equipr-black" : "text-gray-400 hover:text-white"}`}>{l}</button>
                  ))}
                </div>
              </div>
              {activity.length === 0 ? (
                <p className="text-[12px] text-gray-500 bg-white/5 border border-white/10 px-3 py-3" data-testid="field-activity-empty">No sign-ins or dockets in this period.</p>
              ) : (
                <div className="space-y-1.5">
                  {activity.map((ev, i) => {
                    const Icon = ev.type === "docket" ? Truck : ev.type === "sign_out" ? SignOut : SignIn;
                    const st = ev.type === "docket" ? (DK_STATUS[ev.status] || DK_STATUS.submitted) : null;
                    const tappable = ev.type === "docket" && ev.docket_id;
                    return (
                      <button key={i} type="button" onClick={() => tappable && openDocket(ev.docket_id)} disabled={!tappable}
                        data-testid={`field-activity-item-${i}`}
                        className={`w-full flex items-center gap-2.5 bg-white/5 border border-white/10 px-3 py-2 text-left ${tappable ? "hover:border-equipr-yellow cursor-pointer" : "cursor-default"}`}>
                        <span className="w-7 h-7 flex items-center justify-center bg-equipr-yellow text-equipr-black shrink-0"><Icon size={14} weight="bold" /></span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-[13px] font-bold text-white truncate">{ev.title}</span>
                          <span className="block text-[11px] text-gray-400 truncate">{ev.detail}</span>
                        </span>
                        {st && <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full border shrink-0 ${st.cls}`}>{st.label}</span>}
                        <span className="text-[11px] text-gray-500 shrink-0 tabular-nums">{fmtWhen(ev)}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {/* Recent on this device */}
        {recent.length > 0 && (
          <div className="mt-6 space-y-2">
            <p className="text-[11px] font-bold uppercase tracking-widest text-gray-500">Recent on this device</p>
            {recent.map((r, i) => {
              const meta = TYPE_META[r.type];
              const Icon = meta.icon;
              return (
                <Link key={i} to={r.url} data-testid={`field-recent-${i}`}
                  className="flex items-center gap-3 bg-white/5 border border-white/10 hover:border-equipr-yellow px-4 py-3 transition-colors">
                  <span className="w-9 h-9 bg-equipr-yellow text-equipr-black flex items-center justify-center shrink-0"><Icon size={18} weight="bold" /></span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-bold text-white truncate">{r.title}</span>
                    <span className="block text-[11px] text-gray-400 truncate">{meta.label}{r.sub ? ` · ${r.sub}` : ""}</span>
                  </span>
                  <CaretRight size={16} className="text-gray-500 shrink-0" />
                </Link>
              );
            })}
          </div>
        )}

        {!worker && recent.length === 0 && (
          <div className="mt-8 border border-dashed border-white/20 p-6 text-center" data-testid="field-hub-empty">
            <QrCode size={40} className="text-equipr-yellow mx-auto" />
            <p className="text-sm text-gray-300 mt-3 font-semibold">Scan a QR to get started</p>
            <p className="text-xs text-gray-500 mt-1">Scan the QR on your machine (docket) or the site sign (sign-in / diary) — or enter your Worker PIN above.</p>
          </div>
        )}
      </div>

      <div className="px-5 py-4 border-t border-white/10">
        <Link to="/login" className="text-xs text-gray-500 hover:text-equipr-yellow" data-testid="field-hub-office-link">
          Office scheduler or admin? Sign in here →
        </Link>
      </div>

      {viewDocket && (
        <div className="fixed inset-0 z-[70] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setViewDocket(null)} data-testid="docket-view-modal">
          <div className="bg-white w-full sm:max-w-md max-h-[85vh] overflow-auto rounded-t-[16px] sm:rounded-[14px]" onClick={(e) => e.stopPropagation()}>
            {viewDocket.loading ? (
              <div className="p-8 text-center text-gray-500">Loading…</div>
            ) : viewDocket.error ? (
              <div className="p-8 text-center text-red-600">Couldn't load this docket.</div>
            ) : (
              <>
                <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-gray-200 sticky top-0 bg-white">
                  <div>
                    <div className="text-[10.5px] font-bold uppercase tracking-widest text-gray-400">{viewDocket.docket_no || "Docket"}</div>
                    <h3 className="font-extrabold text-lg text-equipr-black leading-tight">{viewDocket.asset_no}</h3>
                  </div>
                  <span className="flex items-center gap-2">
                    {(() => { const st = DK_STATUS[viewDocket.status] || DK_STATUS.submitted; return (
                      <span className={`text-[11px] font-bold uppercase px-2 py-1 rounded-full border ${st.cls.replace("/20", "/10").replace("text-blue-200", "text-blue-700").replace("text-green-200", "text-green-700").replace("text-red-200", "text-red-700").replace("text-gray-200", "text-gray-700")}`} data-testid="docket-view-status">{st.label}</span>
                    ); })()}
                    <button onClick={() => setViewDocket(null)} data-testid="docket-view-close" className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-equipr-black">✕</button>
                  </span>
                </div>
                <div className="p-5 space-y-3 text-[13px]">
                  {viewDocket.off_roster && <div className="bg-amber-50 border border-amber-300 text-amber-800 px-3 py-2 text-[12px]">⚠ Submitted off-roster — under office review.</div>}
                  <Row k="Date" v={viewDocket.date} />
                  <Row k="Project" v={viewDocket.project_name || "—"} />
                  <Row k="Shift" v={viewDocket.shift || "—"} />
                  <Row k="Start → Finish" v={`${viewDocket.start_time || "—"} → ${viewDocket.finish_time || "—"}`} />
                  <Row k="Down time" v={`${viewDocket.down_time_hours || 0} h${viewDocket.down_time_reason ? ` · ${viewDocket.down_time_reason}` : ""}`} />
                  <Row k="Worked hours" v={<b>{viewDocket.worked_hours ?? "—"} h</b>} />
                  {(viewDocket.smu_start != null || viewDocket.smu_finish != null) && <Row k="SMU" v={`${viewDocket.smu_start ?? "—"} → ${viewDocket.smu_finish ?? "—"}`} />}
                  {viewDocket.task_entries?.length > 0 && (
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-1">Tasks</div>
                      <div className="space-y-1">
                        {viewDocket.task_entries.map((t, i) => (
                          <div key={i} className="flex justify-between border border-gray-200 px-2 py-1.5">
                            <span className="truncate">{t.task_name || t.name || t.code || "Task"}</span>
                            <span className="font-semibold shrink-0 ml-2">{t.hours ?? "—"} h</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <Row k="Operator" v={viewDocket.operator_name || viewDocket.submitted_by || "—"} />
                  <Row k="Site rep" v={viewDocket.rep_unavailable ? `Unavailable${viewDocket.rep_reason ? ` · ${viewDocket.rep_reason}` : ""}` : (viewDocket.rep_name || "—")} />
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
